import Seo from "../components/Seo";
import LeadForm from "../components/LeadForm";
import FAQ from "../components/FAQ";
import ServiceCTA from "../components/service/ServiceCTA";
import { KineticLines, Reveal, SectionLabel } from "../components/Reveal";
import { Users, BadgePercent, Building2, FileSearch, ShieldAlert } from "lucide-react";
import { ArrowRight } from "lucide-react";

const AREAS = [
  { icon: Users, title: "Hiring-related tax credits", copy: "Certain hiring activity may qualify for credits — worth checking, especially as your team grows." },
  { icon: BadgePercent, title: "Business tax incentives", copy: "Programs at federal, state, or local levels that may apply to activities you already have." },
  { icon: Building2, title: "Commercial property cost segregation", copy: "Ownership of commercial property can open opportunities worth a specialist's review." },
  { icon: FileSearch, title: "Review of qualifying activities", copy: "A structured look at what your business already does that may qualify — no guesswork." },
];

const FAQS = [
  { q: "What are business tax credits and incentives?", a: "Programs — federal, state, or local — that may reduce tax liability for qualifying activities, such as certain hiring, investments, or property improvements." },
  { q: "Do you guarantee eligibility?", a: "No. Eligibility always depends on your specific circumstances. Independent specialists review your activities and explain what may apply before anything is claimed." },
  { q: "What is cost segregation?", a: "A study of commercial property that may identify opportunities to accelerate certain depreciation deductions. Specialists can evaluate whether your property is a candidate." },
  { q: "Who performs the tax evaluation?", a: "Independent specialists with relevant expertise. Bottom Line Improvements is not a tax advisory firm and does not prepare tax positions or promise outcomes." },
];

function ScannerViz() {
  const rows = [64, 92, 120, 148, 176];
  return (
    <div className="relative rounded-3xl bg-navy-950 border border-white/10 shadow-core p-6 md:p-8 overflow-hidden" data-testid="tax-viz">
      <div className="absolute inset-0 bg-arch-dark" aria-hidden="true" />
      <svg viewBox="0 0 470 250" className="relative w-full h-auto" role="img" aria-label="Animated document scan surfacing potential opportunity categories">
        <rect x="40" y="20" width="190" height="210" rx="14" fill="#FFFFFF" />
        <rect x="66" y="20" width="190" height="210" rx="14" fill="#F4F9FB" stroke="#DCE7EF" />
        {rows.map((y, i) => (
          <g key={y}>
            <rect x="86" y={y} width={i === 2 || i === 4 ? 110 : 140} height="14" rx="7" fill={i === 2 || i === 4 ? "#D7F2EE" : "#EAF4F8"} />
            <rect x={86 + (i === 2 || i === 4 ? 118 : 148)} y={y} width="14" height="14" rx="7" fill={i === 2 || i === 4 ? "#15B8A6" : "#C9DBEA"} />
          </g>
        ))}
        <rect x="66" y="108" width="190" height="3.5" fill="#15B8A6" className="animate-scanLine" style={{ transformBox: "fill-box" }} />
        <text x="66" y="38" fontSize="11" fontWeight="800" letterSpacing="1.6" fill="#234D85" style={{ fontFamily: "Inter, sans-serif" }}>QUALIFYING ACTIVITY REVIEW</text>
        <path d="M262 120 C 310 116, 320 90, 348 86" stroke="#15B8A6" strokeWidth="1.8" fill="none" strokeDasharray="4 6" className="animate-dashFlow" />
        <g>
          <circle cx="390" cy="70" r="40" fill="#152845" stroke="#15B8A6" strokeOpacity="0.6" />
          <text x="390" y="66" textAnchor="middle" fontSize="9.5" fontWeight="800" fill="#FFFFFF" style={{ fontFamily: "Inter, sans-serif" }}>POTENTIAL</text>
          <text x="390" y="80" textAnchor="middle" fontSize="9.5" fontWeight="800" fill="#2BD4C1" style={{ fontFamily: "Inter, sans-serif" }}>OPPORTUNITIES</text>
        </g>
        {["Hiring", "Property", "Incentives"].map((t, i) => (
          <g key={t} style={{ animation: `floatY ${5 + i}s ease-in-out infinite`, animationDelay: `${i * 0.5}s` }}>
            <rect x={316 + i * 6} y={132 + i * 34} width="150" height="26" rx="13" fill="#152845" stroke="#3D6BA8" strokeWidth="1" />
            <text x={330 + i * 6} y={149 + i * 34} fontSize="11" fontWeight="700" fill="#8FB3D9" style={{ fontFamily: "Inter, sans-serif" }}>{t}</text>
          </g>
        ))}
      </svg>
      <p className="relative mt-2 text-center text-xs font-semibold text-ice/50">A specialist review scans your activities — highlights are possibilities, not promises.</p>
    </div>
  );
}

export default function TaxCredits() {
  return (
    <main data-testid="page-tax-credits">
      <Seo
        title="Business Tax Credits & Incentives"
        description="Hiring-related credits, business tax incentives, and commercial property cost segregation — evaluated by independent specialists based on your actual circumstances."
        path="/tax-credits-incentives"
      />

      <section className="relative overflow-hidden bg-offwhite pt-28 lg:pt-36 pb-16 md:pb-24">
        <div className="absolute inset-0 bg-arch-fine" aria-hidden="true" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div>
            <SectionLabel>Business Tax Credits & Incentives</SectionLabel>
            <KineticLines
              as="h1"
              className="mt-5 font-display font-extrabold tracking-tightest text-navy text-4xl sm:text-5xl leading-[1.06]"
              lines={["Discover Opportunities", <>You May Have <span className="text-teal-dark">Overlooked.</span></>]}
            />
            <Reveal delay={0.15}>
              <p className="mt-6 max-w-xl text-base md:text-lg leading-relaxed text-slate">
                Depending on your business activities, hiring, investments, or property ownership, you may qualify for
                certain tax credits or incentives. We connect you with specialists who can help evaluate potential
                opportunities.
              </p>
            </Reveal>
            <Reveal delay={0.25}>
              <a href="#tax-form" data-testid="tax-hero-cta" className="group mt-8 inline-flex items-center gap-2 bg-navy text-white font-bold text-[15px] px-7 py-4 rounded-full transition-all duration-300 hover:bg-teal hover:text-navy shadow-lift">
                Check Potential Opportunities
                <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" />
              </a>
            </Reveal>
          </div>
          <Reveal delay={0.2}><ScannerViz /></Reveal>
        </div>
      </section>

      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <SectionLabel>What Specialists Can Evaluate</SectionLabel>
            <KineticLines as="h2" className="mt-5 font-display font-extrabold tracking-tightest text-navy text-3xl sm:text-4xl leading-[1.1]" lines={["Opportunities exist in what your business already does."]} />
          </div>
          <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 gap-5">
            {AREAS.map((a, i) => (
              <Reveal key={a.title} delay={i * 0.05} className="h-full">
                <div className="group h-full rounded-2xl border border-line bg-white p-6 md:p-7 transition-all duration-300 hover:-translate-y-1 hover:shadow-lift hover:border-teal/50 flex gap-5" data-testid={`tax-area-${i + 1}`}>
                  <span className="inline-flex items-center justify-center w-12 h-12 rounded-xl bg-ice group-hover:bg-teal-soft transition-colors shrink-0">
                    <a.icon className="w-5 h-5 text-royal group-hover:text-teal-dark transition-colors" />
                  </span>
                  <div>
                    <h3 className="font-display font-extrabold tracking-tight text-navy text-lg">{a.title}</h3>
                    <p className="mt-2 text-sm leading-relaxed text-slate">{a.copy}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal>
            <div className="mt-10 rounded-2xl border border-teal/40 bg-teal-soft/60 p-6 flex items-start gap-4" data-testid="tax-disclaimer-note">
              <ShieldAlert className="w-5 h-5 text-teal-dark shrink-0 mt-0.5" />
              <p className="text-sm md:text-[15px] leading-relaxed text-navy">
                <strong>Honest fine print:</strong> eligibility depends on your specific circumstances, and no outcome
                — credit, refund, or saving — can be promised in advance. Independent specialists evaluate first;
                decisions come after.
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="py-16 md:py-24 bg-ice/60">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-2 gap-12">
          <div>
            <SectionLabel>Who This Helps</SectionLabel>
            <KineticLines as="h2" className="mt-5 font-display font-extrabold tracking-tightest text-navy text-3xl leading-[1.1]" lines={["Especially relevant if you…"]} />
            <Reveal delay={0.1}>
              <ul className="mt-5 space-y-3 text-base leading-relaxed text-slate">
                {["Hired new team members recently, or plan to", "Own or lease commercial property", "Invested in equipment, facilities, or improvements", "Never had a specialist review qualifying activities"].map((t) => (
                  <li key={t} className="flex items-start gap-3">
                    <span className="mt-2 w-2 h-2 rounded-full bg-teal shrink-0" aria-hidden="true" />
                    {t}
                  </li>
                ))}
              </ul>
            </Reveal>
          </div>
          <div>
            <SectionLabel>How The Process Works</SectionLabel>
            <div className="mt-6 space-y-6">
              {[
                ["Share your activities", "A short summary of hiring, property, and investments — nothing sensitive."],
                ["A specialist reviews", "An independent specialist evaluates which programs may apply to your business."],
                ["You decide next steps", "If something applies, the specialist explains the path before anything happens."],
              ].map(([t, c], i) => (
                <Reveal key={t} delay={i * 0.08}>
                  <div className="flex gap-4">
                    <span className="flex items-center justify-center w-9 h-9 rounded-full bg-navy text-teal-bright text-xs font-extrabold shrink-0">{i + 1}</span>
                    <div>
                      <h3 className="font-display font-extrabold text-navy">{t}</h3>
                      <p className="mt-1 text-sm leading-relaxed text-slate">{c}</p>
                    </div>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </section>

      <FAQ items={FAQS} title="Tax Opportunity Questions" label="FAQ" />

      <section id="tax-form" className="py-16 md:py-24 bg-white scroll-mt-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div>
            <SectionLabel>Check Opportunities</SectionLabel>
            <KineticLines as="h2" className="mt-5 font-display font-extrabold tracking-tightest text-navy text-3xl sm:text-4xl leading-[1.1]" lines={["A five-minute question could be worth a specialist's review."]} />
            <Reveal delay={0.1}>
              <p className="mt-5 text-base leading-relaxed text-slate max-w-lg">
                Tell us a little about your business and we'll help you connect with specialists who evaluate tax
                credits and incentives. No eligibility promises — just an honest evaluation.
              </p>
            </Reveal>
          </div>
          <Reveal delay={0.1}><LeadForm service="tax" heading="Check Potential Opportunities" /></Reveal>
        </div>
      </section>

      <ServiceCTA
        service="tax"
        title={["Some opportunities only appear", "when someone knows where to look."]}
        copy="Start the conversation. Independent specialists can evaluate what may apply to your business."
        cta="Check Potential Opportunities"
      />
    </main>
  );
}
